import { type FC } from 'react';
import { CalendarClock, CalendarX2, Flag, Radio } from 'lucide-react';

import type { TLotSaleState } from '@/shared/auctionTypes';
import { cn } from '@/utils/cn';

/**
 * Where the sale is: scheduled, running now, or over (spec 010).
 *
 * Until this existed a lot showed only a date, so a lane running AT THIS MOMENT
 * looked identical to one scheduled next week — the same confusion that made
 * `diff_minutes < 0` read as "finished" and quietly deleted every live auction
 * from the listings.
 *
 * `null` renders nothing. The provider does not always send a state, and an
 * invented status on a page about a real purchase is exactly what Principle I
 * forbids.
 *
 * A lot the provider still calls upcoming whose sale day has already passed —
 * the mirror lags the yard by up to a day, and about 7% of "active" lots carry
 * a date months back — must not wear "Zaplanowana" next to a struck-through
 * date (seen 2026-09-14). `stale` turns that pair into an amber "Termin
 * minął": the same words the date bar uses, so the card says one thing.
 *
 * Colour is never the only signal: each state carries its own words and icon,
 * so the chip survives greyscale and a screen reader equally. The fills are
 * dark, saturated tints (not translucent washes) because the chip sits on top
 * of an auction-yard photo, which can be anything from asphalt to a white
 * bonnet in full sun — it has to read on all of them (spec 013).
 */

type TChipLook = { text: string; className: string; opaqueClassName: string; title: string; Icon: typeof Radio };

const LABELS: Record<TLotSaleState, TChipLook> = {
  live: {
    text: 'Trwa licytacja',
    // The only state with a solid, saturated fill — it is the only one that is
    // time-critical. Green, not the brand gold: gold already means "price" and
    // "action" everywhere on the card, and a live lane must read as GO.
    className:
      'border-emerald-300 bg-emerald-500 text-emerald-950 shadow-[0_0_20px_-4px_rgba(16,185,129,0.7)]',
    opaqueClassName: 'border-emerald-300 bg-emerald-500 text-emerald-950',
    title: 'Aukcja tego lotu odbywa się w tej chwili',
    Icon: Radio,
  },
  upcoming: {
    // The common case, so it must not shout: a calm blue — informational, not
    // a warning (red and amber are reserved for finished lots and notices) —
    // distinct from the neutral source label beside it.
    text: 'Zaplanowana',
    className: 'border-sky-400/40 bg-sky-950/85 text-sky-200',
    opaqueClassName: 'border-sky-400/40 bg-sky-950 text-sky-200',
    title: 'Licytacja jeszcze się nie rozpoczęła',
    Icon: CalendarClock,
  },
  finished: {
    text: 'Zakończona',
    className: 'border-red-400/40 bg-red-950/85 text-red-200',
    opaqueClassName: 'border-red-400/40 bg-red-950 text-red-200',
    title: 'Licytacja już się odbyła',
    Icon: Flag,
  },
};

/** Amber, like the card's other notices: a date that needs confirming, not a warning about the car. */
const STALE_UPCOMING: TChipLook = {
  text: 'Termin minął',
  className: 'border-amber-400/40 bg-amber-950/85 text-amber-200',
  opaqueClassName: 'border-amber-400/40 bg-amber-950 text-amber-200',
  title: 'Podany termin licytacji już minął, a lot wciąż figuruje jako zaplanowany — potwierdzimy przed licytacją',
  Icon: CalendarX2,
};

type TAuctionLotStateChip = {
  state: TLotSaleState | null;
  /** The sale date is already behind us (the caller's `isPast`); an upcoming lot then reads "Termin minął". */
  stale?: boolean;
  className?: string;
  /**
   * Solid fill, no blur, no shadow — for the card's joined strip, where the
   * chip overlaps the source label along a seam and a translucent fill would
   * show the label through as a second wedge.
   */
  opaque?: boolean;
};

export const AuctionLotStateChip: FC<TAuctionLotStateChip> = ({ state, stale = false, className = '', opaque = false }) => {
  if (!state) return null;

  const { text, className: tone, opaqueClassName, title, Icon } = stale && state === 'upcoming' ? STALE_UPCOMING : LABELS[state];

  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-oswald uppercase tracking-wider',
        opaque ? opaqueClassName : `${tone} backdrop-blur-md shadow-lg shadow-black/30`,
        className
      )}
    >
      {state === 'live' ? (
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-950 motion-safe:animate-pulse"
        />
      ) : (
        <Icon size={12} aria-hidden />
      )}
      {text}
    </span>
  );
};
